import { useCallback, useEffect, useState } from 'react';

/**
 * useState whose value is persisted to window.localStorage under the given key.
 * Serialisation is JSON-based; reads are best-effort (falls back to defaultValue on parse error).
 *
 * Example:
 *   const [viewMode, setViewMode] = useLocalStorageState<ViewMode>('vp_caisse_view_mode', 'grid-sm');
 */
export function useLocalStorageState<T>(
  key: string,
  defaultValue: T,
): [T, (v: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    if (typeof window === 'undefined') return defaultValue;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw === null) return defaultValue;
      return JSON.parse(raw) as T;
    } catch {
      return defaultValue;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {}
  }, [key, value]);

  const set = useCallback((v: T | ((prev: T) => T)) => {
    setValue((prev) => (typeof v === 'function' ? (v as (p: T) => T)(prev) : v));
  }, []);

  return [value, set];
}
